import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Dialog } from "components/Dialog";
import { FacilityPicker } from "components/FacilityPicker";
import {
  getStepperSubmissionCombo,
  getWasteSubmissionFormName,
} from "encamp-shared/src/submissions/wasteSubmissionForms";
import {
  FacilityPickerFragment,
  SubmissionMethod,
  SubmissionType,
} from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useState } from "react";

import { isSubmissionMethod, isSubmissionType } from "./submissionEnumGuards";

export type CreateStepperArgs = {
  submissionType: SubmissionType;
  submissionMethod: SubmissionMethod;
  facilityId: string;
  title: string;
  dueDate: DateTime;
};

type SubmissionFlow = "stepper" | "documentReview";

// The 8700-12 combo from the shared single source of truth — the dialog never
// hardcodes a (type, method) pair that could drift from it.
const STEPPER_COMBO = getStepperSubmissionCombo();

// Display name derived from the same shared source of truth; the fallback only
// applies if the stepper combo ever stops resolving to a known form.
const STEPPER_FORM_NAME =
  getWasteSubmissionFormName(STEPPER_COMBO.type, STEPPER_COMBO.method) ?? "EPA Form 8700-12";

/**
 * Flow picker shown from "Create Submission" when the waste submission stepper
 * is enabled. Instead of asking for the (type, method) combo that happens to
 * map to the 8700-12, it presents the choice directly:
 *
 * - "EPA Form 8700-12" collects just title, facility, and due date (the
 *   type/method combo comes from the shared source of truth), creates the
 *   data-entry submission, and drops the user into the stepper.
 * - "All other submission types" routes to the document-review create form,
 *   where the type, method, and remaining fields are entered as before.
 *
 * When the stepper flag is off, the caller skips this dialog entirely.
 */
export function CreateSubmissionTypeDialog({
  open,
  onClose,
  onContinueLegacy,
  onCreateStepper,
  creating = false,
}: {
  open: boolean;
  onClose: () => void;
  onContinueLegacy: () => void;
  onCreateStepper: (args: CreateStepperArgs) => void;
  creating?: boolean;
}) {
  const { tenantId } = useTenant();
  const [flow, setFlow] = useState<SubmissionFlow | "">("");
  const [title, setTitle] = useState("");
  const [facility, setFacility] = useState<FacilityPickerFragment | null>(null);
  const [dueDate, setDueDate] = useState<DateTime | null>(null);

  const canCreateStepper = !!title.trim() && !!facility && !!dueDate;

  const reset = () => {
    setFlow("");
    setTitle("");
    setFacility(null);
    setDueDate(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreateStepper = () => {
    if (!facility || !dueDate || !title.trim()) {
      return;
    }
    // The guards always pass — the Prisma and GraphQL enums share their string
    // values — but narrowing this way avoids an `as` cast.
    if (!isSubmissionType(STEPPER_COMBO.type) || !isSubmissionMethod(STEPPER_COMBO.method)) {
      return;
    }
    onCreateStepper({
      submissionType: STEPPER_COMBO.type,
      submissionMethod: STEPPER_COMBO.method,
      facilityId: facility.id,
      title: title.trim(),
      dueDate,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      title="Create Submission"
      description="Select which type of waste submission to create"
      // The Figma spec stacks full-width actions under the form, so the
      // buttons render in the content area instead of the standard action row.
      noPrimaryAction
    >
      <RadioGroup
        aria-label="Submission flow"
        value={flow}
        onChange={(e) => {
          const value = e.target.value;
          if (value === "stepper" || value === "documentReview") {
            setFlow(value);
          }
        }}
      >
        <FormControlLabel
          value="stepper"
          control={<Radio />}
          sx={{ alignItems: "flex-start", mb: 1, "& .MuiRadio-root": { pt: 0 } }}
          label={
            <Stack>
              <Typography variant="body1">{STEPPER_FORM_NAME}</Typography>
              <Typography variant="caption" color="text.secondary">
                Fill out the RCRAInfo myRCRAid form step by step in Encamp.
              </Typography>
            </Stack>
          }
        />
        <FormControlLabel
          value="documentReview"
          control={<Radio />}
          sx={{ alignItems: "flex-start", "& .MuiRadio-root": { pt: 0 } }}
          label={
            <Stack>
              <Typography variant="body1">All other submission types</Typography>
              <Typography variant="caption" color="text.secondary">
                Choose the submission type and method, then upload the filing for document review.
              </Typography>
            </Stack>
          }
        />
      </RadioGroup>

      {flow === "stepper" && (
        <>
          <TextField
            label="Title"
            required
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <FacilityPicker
            label="Facility *"
            facilityQueryVariant="waste"
            defaultSearchTerm={tenantId ? `tenantId:${tenantId}` : ""}
            showErrors={false}
            value={facility}
            onChange={setFacility}
            invalid={false}
            isDirty={false}
            isTouched={false}
            isValidating={false}
            error={undefined}
          />

          <DatePicker
            label="Due date *"
            value={dueDate}
            onChange={setDueDate}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </>
      )}

      <Stack sx={{ gap: 1, pt: 1 }}>
        {flow === "stepper" ? (
          <Button
            variant="contained"
            fullWidth
            loading={creating}
            disabled={!canCreateStepper}
            onClick={handleCreateStepper}
          >
            Continue in stepper
          </Button>
        ) : (
          <Button variant="contained" fullWidth disabled={flow === ""} onClick={onContinueLegacy}>
            Next
          </Button>
        )}
        <Button fullWidth onClick={handleClose}>
          Cancel
        </Button>
      </Stack>
    </Dialog>
  );
}
